import { z } from "zod";
/**
 * Portal workspace schemas.
 *
 * A workspace is the tenant-local commercial wrapper for the client portal.
 * L30 Premium and Client Hub use the same portal engine but differ in package
 * expression, naming, and light white-label settings.
 */
export const PortalWorkspacePackageKindSchema = z.enum(["l30_premium", "client_hub"]);
export const PortalWorkspacePurposeSchema = z.enum(["relationship", "programme"]);
export const PORTAL_CAPABILITY_KEYS = [
    "engagement_plan",
    "projects",
    "retainers",
    "meetings",
    "documents",
    "document_review",
    "uploads",
    "conversations",
    "approvals",
    "signatures",
    "invoices",
    "programmes",
    "working_groups",
    "board_workspace",
    "board_packs",
    "supplier_workflow",
    "evidence",
    "ask",
];
export const PortalCapabilityKeySchema = z.enum(PORTAL_CAPABILITY_KEYS);
export const L30_PREMIUM_PORTAL_CAPABILITIES = [
    "engagement_plan",
    "projects",
    "meetings",
    "documents",
    "document_review",
    "uploads",
    "conversations",
    "approvals",
    "signatures",
];
export const CLIENT_HUB_PORTAL_CAPABILITIES = [
    "documents",
    "uploads",
    "conversations",
    "approvals",
];
export const PROGRAMME_PORTAL_CAPABILITIES = [
    "engagement_plan",
    "meetings",
    "documents",
    "document_review",
    "uploads",
    "conversations",
    "approvals",
    "signatures",
    "programmes",
    "working_groups",
    "evidence",
    "ask",
];
export const PortalCapabilityListSchema = z
    .array(PortalCapabilityKeySchema)
    .max(PORTAL_CAPABILITY_KEYS.length)
    .refine((value) => new Set(value).size === value.length, "Capabilities cannot be repeated")
    .superRefine((value, ctx) => {
    const enabled = new Set(value);
    for (const capability of ["document_review", "signatures"]) {
        if (enabled.has(capability) && !enabled.has("documents")) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${capability} requires documents`,
            });
        }
    }
    if (enabled.has("working_groups") && !enabled.has("programmes")) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "working_groups requires programmes",
        });
    }
});
export function portalCapabilityPreset(purpose, packageKind) {
    if (purpose === "programme")
        return [...PROGRAMME_PORTAL_CAPABILITIES];
    if (packageKind === "client_hub")
        return [...CLIENT_HUB_PORTAL_CAPABILITIES];
    return [...L30_PREMIUM_PORTAL_CAPABILITIES];
}
const PortalAccentColorSchema = z
    .preprocess((value) => (value === "" ? null : value), z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .nullable())
    .default(null);
const OptionalStringToNullSchema = z
    .preprocess((value) => (typeof value === "string" && value.trim() === "" ? null : value), z.string().trim().min(1).max(1000).nullable())
    .default(null);
export const PortalWorkspaceSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    packageKind: PortalWorkspacePackageKindSchema.default("l30_premium"),
    purpose: PortalWorkspacePurposeSchema.default("relationship"),
    capabilities: PortalCapabilityListSchema,
    portalEnabled: z.boolean().default(true),
    accentColor: PortalAccentColorSchema,
    logoBlobKey: OptionalStringToNullSchema,
    companyId: z.string().uuid().nullable(),
    status: z.string().min(1).max(20),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const PortalWorkspaceListResponseSchema = z.object({
    workspaces: z.array(PortalWorkspaceSchema),
});
export const PortalWorkspaceProvisionSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
    packageKind: PortalWorkspacePackageKindSchema.default("l30_premium"),
    purpose: PortalWorkspacePurposeSchema.default("relationship"),
    capabilities: PortalCapabilityListSchema.optional(),
    portalEnabled: z.boolean().default(true),
    accentColor: PortalAccentColorSchema.optional(),
    logoBlobKey: OptionalStringToNullSchema.optional(),
});
export const PortalWorkspaceUpdateSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    packageKind: PortalWorkspacePackageKindSchema,
    purpose: PortalWorkspacePurposeSchema,
    capabilities: PortalCapabilityListSchema,
    portalEnabled: z.boolean(),
    accentColor: PortalAccentColorSchema,
    logoBlobKey: OptionalStringToNullSchema,
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, {
    message: "At least one workspace field must be supplied",
});
export const PortalWorkspaceResponseSchema = z.object({
    workspace: PortalWorkspaceSchema,
});
//# sourceMappingURL=portal-workspace.js.map